import React from "react";
import PropTypes from "prop-types";
import Page from "src/components/Page";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Typography, Container, Stack, Chip } from "@material-ui/core";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import useAuth from "src/hooks/useAuth";
import useCanDo from "src/hooks/useCanDo";
import { openPinDialog } from "src/redux/slices/employeeSession";

// F-role-gates · 2026-08-25 · Fallback visual cuando un empleado intenta
// entrar por URL directa a una sección para la que su rol no tiene
// permiso. Reemplaza al Page401View en el caso empleado→acción, porque:
//   • Ofrece CTA "Solicitar acceso" que abre el PIN dialog del supervisor
//     (mismo flujo del override 403 del backend).
//   • Contextualiza con el nombre del rol actual del empleado en vez de
//     un genérico "401 Unauthorized" que asusta.
//   • Ofrece "Volver al inicio" a la ruta correcta según isOnlyTpv.
//
// Usado por `RequireAction` (guard nuevo). Sin argumentos hace un fallback
// genérico; con `action` incluye la acción concreta en el mensaje para
// que el supervisor sepa qué autorizar.

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: "100%",
    alignItems: "center",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(10),
  },
  iconWrap: {
    display: "inline-flex",
    padding: theme.spacing(2.5),
    borderRadius: "50%",
    backgroundColor: theme.palette.warning.lighter || "rgba(255, 193, 7, 0.12)",
    color: theme.palette.warning.darker || "#7a4f01",
    marginBottom: theme.spacing(2),
  },
}));

NoPermissionView.propTypes = {
  action: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  sectionLabel: PropTypes.string,
};

function NoPermissionView({ action, sectionLabel }) {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { employeeName, roleName } = useCanDo();

  const actions = Array.isArray(action) ? action : action ? [action] : [];
  const backTo = user?.isOnlyTpv ? "/app/tpv/camarero/carta" : "/app";

  // Abre el PIN dialog en modo "elevación de sesión" — el supervisor
  // introduce su PIN, loginByPin persiste su token y useCanDo reevalúa.
  // No reejecutamos ninguna request (no hay una pendiente) — solo
  // recargamos la ruta para forzar el rerender con los permisos nuevos.
  // Nota: este flujo eleva la sesión al supervisor de forma persistente
  // hasta que él cierre — a diferencia del override transient de axios
  // que restaura al empleado original. Para navegación admin puntual
  // esto es lo correcto: el supervisor entra y trabaja unos minutos.
  const handleRequestAccess = () => {
    dispatch(openPinDialog({
      reason: sectionLabel
        ? `Se requiere autorización para acceder a ${sectionLabel}`
        : "Se requiere autorización de un supervisor",
      action: actions[0] || null,
      showEmployeeSelector: true,
      onSuccess: () => { history.go(0); },
      onCancel: () => { /* usuario canceló, se queda en la vista */ },
    }));
  };

  return (
    <Page title="Sin permisos | TakeMyOrder" className={classes.root}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: "center" }}>
          <Box className={classes.iconWrap}>
            <LockOutlinedIcon sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            Esta sección está restringida
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3 }}>
            {employeeName ? (
              <>
                Hola <strong>{employeeName}</strong>, tu rol
                {roleName ? <> <strong>{roleName}</strong></> : null} no incluye
                permiso para {sectionLabel ? <>acceder a <strong>{sectionLabel}</strong></> : "esta sección"}.
                Pide autorización a un supervisor o vuelve al inicio.
              </>
            ) : (
              <>No tienes permiso para acceder a esta sección. Contacta con el administrador.</>
            )}
          </Typography>

          {actions.length > 0 && (
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 3 }}>
              {actions.map((a) => (
                <Chip key={a} label={a} size="small" variant="outlined" sx={{ fontFamily: "monospace" }} />
              ))}
            </Stack>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button
              size="large"
              variant="contained"
              startIcon={<SupervisorAccountIcon />}
              onClick={handleRequestAccess}
              sx={{ fontWeight: 700 }}
            >
              Solicitar acceso al supervisor
            </Button>
            <Button
              to={backTo}
              size="large"
              variant="outlined"
              component={RouterLink}
            >
              Volver al inicio
            </Button>
          </Stack>
        </Box>
      </Container>
    </Page>
  );
}

export default NoPermissionView;
